/**
 * FairwayLive brand + South African golf theming constants.
 *
 * IMAGERY POLICY: every hero entry here is a CSS-generated gradient scene, not a
 * photograph. No real course, tournament or club imagery is bundled. When real
 * art is commissioned it must be AI-generated or royalty-free licensed, and the
 * scene names below stay fictional. See docs/theming.md.
 */
export const BRAND = {
  name: 'FairwayLive',
  shortName: 'FL',
  tagline: 'Live golf scoring, built for the clubhouse and the sponsor.',
  supportEmail: 'hello@fairwaylive.example',
  currency: 'ZAR',
  currencySymbol: 'R',
  locale: 'en-ZA',
} as const;

/** Broadcast palette: deep greens, fairway golds, highveld sky, sunset amber. */
export const PALETTE = {
  fairwayDeep: '#04301f',
  fairwayMid: '#0b5236',
  fairwayLight: '#1c7a4f',
  greenGlow: '#3ddc84',
  bushveldGold: '#d9a227',
  sunsetAmber: '#f2762e',
  sunsetRose: '#c2415c',
  skyHighveld: '#2f80c9',
  skyPale: '#a9d6f2',
  bunkerSand: '#e8d9b5',
  slate900: '#08110d',
  slate800: '#0f1c16',
  slate700: '#16281f',
  chalk: '#f4f7f4',
} as const;

export interface HeroScene {
  key: string;
  /** Fictional location name. Never a real course. */
  title: string;
  subtitle: string;
  /** CSS background value — layered gradients render the scene, no image files. */
  css: string;
  /** Accent used for overlaid type on this scene. */
  accent: string;
}

export const HERO_SCENES: HeroScene[] = [
  {
    key: 'drakensberg_dawn',
    title: 'Mountain Reach Estate',
    subtitle: 'Escarpment dawn, hole 7',
    accent: PALETTE.greenGlow,
    css: [
      'radial-gradient(120% 80% at 20% 8%, rgba(169,214,242,0.55) 0%, rgba(47,128,201,0.12) 42%, transparent 70%)',
      'linear-gradient(190deg, #1c3f5c 0%, #2f6b7a 26%, #17593a 55%, #0a3d26 78%, #04301f 100%)',
      'radial-gradient(70% 40% at 78% 34%, rgba(8,17,13,0.55) 0%, transparent 60%)',
    ].join(','),
  },
  {
    key: 'bushveld_noon',
    title: 'Marula Ridge Country Club',
    subtitle: 'Bushveld crossing, hole 12',
    accent: PALETTE.bushveldGold,
    css: [
      'radial-gradient(100% 70% at 70% 12%, rgba(242,225,168,0.5) 0%, transparent 62%)',
      'linear-gradient(185deg, #7fb0d6 0%, #cbb375 34%, #8f8f3f 58%, #2f6b32 80%, #0b5236 100%)',
      'radial-gradient(60% 50% at 22% 70%, rgba(4,48,31,0.5) 0%, transparent 65%)',
    ].join(','),
  },
  {
    key: 'coastal_wind',
    title: 'Cape Point Links',
    subtitle: 'Ocean carry, hole 16',
    accent: PALETTE.skyPale,
    css: [
      'radial-gradient(120% 70% at 35% 0%, rgba(215,240,255,0.6) 0%, transparent 58%)',
      'linear-gradient(180deg, #9fd0ea 0%, #4d9fc4 30%, #1f7a8c 52%, #14634a 74%, #073a26 100%)',
      'radial-gradient(80% 30% at 80% 62%, rgba(255,255,255,0.22) 0%, transparent 55%)',
    ].join(','),
  },
  {
    key: 'sunset_finish',
    title: 'Aloe Valley Golf Club',
    subtitle: 'Last light on 18',
    accent: PALETTE.sunsetAmber,
    css: [
      'radial-gradient(90% 60% at 62% 18%, rgba(255,196,110,0.75) 0%, rgba(242,118,46,0.35) 38%, transparent 68%)',
      'linear-gradient(188deg, #6a3b6e 0%, #c2415c 22%, #f2762e 42%, #7a5a26 66%, #123a25 88%, #04301f 100%)',
      'radial-gradient(70% 40% at 18% 78%, rgba(8,17,13,0.6) 0%, transparent 62%)',
    ].join(','),
  },
  {
    key: 'winelands_morning',
    title: 'Stone Kloof Vineyards Course',
    subtitle: 'Valley fog, hole 3',
    accent: PALETTE.greenGlow,
    css: [
      'radial-gradient(110% 70% at 28% 10%, rgba(238,246,255,0.6) 0%, transparent 55%)',
      'linear-gradient(184deg, #c8dced 0%, #8fb6a4 26%, #4d8a5d 50%, #1f6b41 74%, #063a24 100%)',
      'radial-gradient(60% 40% at 76% 66%, rgba(4,48,31,0.45) 0%, transparent 60%)',
    ].join(','),
  },
];

/** Fictional names used by the demo generator and all sample content. */
export const FICTIONAL = {
  tournaments: [
    'Aloe Valley Invitational',
    'Marula Ridge Classic',
    'Cape Point Links Championship',
    'Highveld Winter Series',
    'Karoo Open',
    'Sable River Masters',
  ],
  clubs: [
    'Aloe Valley Golf Club',
    'Marula Ridge Country Club',
    'Cape Point Links',
    'Stone Kloof Vineyards Course',
    'Mountain Reach Estate',
    'Sable River Golf Club',
  ],
  sponsors: [
    'Kalahari Motors',
    'Protea Financial',
    'Highveld Insurance',
    'Table Bay Logistics',
    'Sable River Brewing',
    'Vaal Energy',
    'Cape Fibre',
    'Umhlanga Property Group',
  ],
} as const;
